import React, { useState, useEffect } from 'react';
import { apiGet, apiPost, apiPut } from '../utils/api';

interface TelegramGroup {
  chatId: string;
  type: string;
  title?: string;
  username?: string;
  messageCount: number;
  lastMessageDate?: string;
  ignored: boolean;
}

interface GroupTopic {
  id: string;
  threadId: string;
  name: string | null;
  createdAt?: string;
  updatedAt?: string;
}

interface GroupSettings {
  id?: string;
  chatId: string;
  type: string;
  title?: string | null;
  username?: string | null;
  loggingEnabled: boolean;
  logOnlyCommands: boolean;
  logOnlyMentions: boolean;
  botResponsesEnabled: boolean;
  respondOnlyToMentions: boolean;
  autoResponses: Record<string, string> | null;
  topics?: GroupTopic[];
  createdAt?: string;
  updatedAt?: string;
}

const TelegramGroupSettings: React.FC = () => {
  const [groups, setGroups] = useState<TelegramGroup[]>([]);
  const [groupSettingsMap, setGroupSettingsMap] = useState<Map<string, GroupSettings>>(new Map());
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<TelegramGroup | null>(null);
  const [editingSettings, setEditingSettings] = useState<GroupSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [autoResponseCommand, setAutoResponseCommand] = useState('');
  const [autoResponseText, setAutoResponseText] = useState('');
  const [topics, setTopics] = useState<GroupTopic[]>([]);
  const [editingTopicId, setEditingTopicId] = useState<string | null>(null);
  const [editingTopicName, setEditingTopicName] = useState('');
  const TELEGRAM_MESSAGES_LIMIT = 500;

  useEffect(() => {
    fetchGroupsAndSettings();
  }, []);

  const fetchGroupsAndSettings = async () => {
    try {
      setLoading(true);

      // Важно: ограничиваем выборку логов, чтобы не нагружать систему.
      // Ранее настроенные группы подтягиваем из настроек отдельно.
      const [data, settings] = await Promise.all([
        apiGet<{ messages: any[]; pagination: any }>(`/telegram-messages?limit=${TELEGRAM_MESSAGES_LIMIT}`),
        apiGet<GroupSettings[]>('/telegram-group-settings')
      ]);

      const settingsMap = new Map<string, GroupSettings>();
      settings.forEach((s: GroupSettings) => {
        settingsMap.set(s.chatId, s);
      });
      setGroupSettingsMap(settingsMap);
      
      // Группируем по chatId и извлекаем информацию о группах
      const groupMap = new Map<string, TelegramGroup>();
      
      data.messages?.forEach((msg: any) => {
        try {
          const metadata = msg.metadata ? JSON.parse(msg.metadata) : {};
          if (metadata.message?.chat && metadata.message.chat.type !== 'private') {
            const chat = metadata.message.chat;
            const chatId = chat.id.toString();
            
            if (!groupMap.has(chatId)) {
              groupMap.set(chatId, {
                chatId: chatId,
                type: chat.type || 'unknown',
                title: chat.title || null,
                username: chat.username || null,
                messageCount: 0,
                lastMessageDate: msg.createdAt,
                ignored: metadata.ignored !== false
              });
            }
            
            const group = groupMap.get(chatId)!;
            group.messageCount++;
            if (new Date(msg.createdAt) > new Date(group.lastMessageDate || 0)) {
              group.lastMessageDate = msg.createdAt;
            }
          }
        } catch (e) {
          // Игнорируем ошибки парсинга
        }
      });

      // Добавляем группы из сохраненных настроек, даже если по ним
      // нет сообщений в текущем окне логов.
      settings.forEach((s) => {
        if (!s.chatId) return;
        const chatId = s.chatId.toString();
        if (!groupMap.has(chatId)) {
          groupMap.set(chatId, {
            chatId,
            type: s.type || 'unknown',
            title: s.title || null,
            username: s.username || null,
            messageCount: 0,
            lastMessageDate: undefined,
            ignored: false
          });
        } else {
          const existing = groupMap.get(chatId)!;
          if (!existing.title && s.title) existing.title = s.title;
          if (!existing.username && s.username) existing.username = s.username;
          if ((!existing.type || existing.type === 'unknown') && s.type) existing.type = s.type;
        }
      });
      
      const groupsList = Array.from(groupMap.values()).sort((a, b) => {
        const dateA = a.lastMessageDate ? new Date(a.lastMessageDate).getTime() : 0;
        const dateB = b.lastMessageDate ? new Date(b.lastMessageDate).getTime() : 0;
        if (dateB !== dateA) return dateB - dateA;
        return String(a.title || a.username || a.chatId).localeCompare(String(b.title || b.username || b.chatId), 'ru');
      });
      
      setGroups(groupsList);
    } catch (error) {
      console.error('Ошибка загрузки групп Telegram:', error);
      setGroups([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredGroups = groups.filter(group => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      group.title?.toLowerCase().includes(query) ||
      group.username?.toLowerCase().includes(query) ||
      group.chatId.includes(query)
    );
  });

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTypeLabel = (type: string) => {
    const labels: { [key: string]: string } = {
      'group': 'Группа',
      'supergroup': 'Супергруппа',
      'channel': 'Канал'
    };
    return labels[type] || type;
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'group':
        return 'bg-blue-100 text-blue-800';
      case 'supergroup':
        return 'bg-purple-100 text-purple-800';
      case 'channel':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleOpenSettings = async (group: TelegramGroup) => {
    setSelectedGroup(group);
    const existingSettings = groupSettingsMap.get(group.chatId);
    
    if (existingSettings) {
      setEditingSettings({ ...existingSettings });
    } else {
      setEditingSettings({
        chatId: group.chatId,
        type: group.type,
        title: group.title || null,
        username: group.username || null,
        loggingEnabled: true,
        logOnlyCommands: false,
        logOnlyMentions: false,
        botResponsesEnabled: false,
        respondOnlyToMentions: true,
        autoResponses: null
      });
    }
    
    // Загружаем топики группы
    try {
      const response = await apiGet<{ topics: GroupTopic[]; mainThreadUnreadCount?: number }>(`/telegram-group-settings/${group.chatId}/topics`);
      // API возвращает объект с полем topics, а не массив напрямую
      const topicsArray = response && typeof response === 'object' && 'topics' in response 
        ? (Array.isArray(response.topics) ? response.topics : [])
        : (Array.isArray(response) ? response : []);
      setTopics(topicsArray);
    } catch (error) {
      console.error('Ошибка загрузки топиков:', error);
      setTopics([]);
    }
    
    setAutoResponseCommand('');
    setAutoResponseText('');
    setEditingTopicId(null);
    setEditingTopicName('');
    setShowSettingsModal(true);
  };

  const handleSaveSettings = async () => {
    if (!editingSettings) return;
    
    try {
      setSaving(true);
      
      // Подготавливаем autoResponses
      let autoResponses = editingSettings.autoResponses || {};
      if (autoResponseCommand && autoResponseText) {
        autoResponses[autoResponseCommand.toLowerCase()] = autoResponseText;
      }
      
      const settingsToSave = {
        ...editingSettings,
        autoResponses: Object.keys(autoResponses).length > 0 ? autoResponses : null
      };
      
      await apiPost('/telegram-group-settings', settingsToSave);
      
      // Обновляем локальное состояние
      const newSettingsMap = new Map(groupSettingsMap);
      newSettingsMap.set(editingSettings.chatId, settingsToSave);
      setGroupSettingsMap(newSettingsMap);
      
      setShowSettingsModal(false);
      setSelectedGroup(null);
      setEditingSettings(null);
      setAutoResponseCommand('');
      setAutoResponseText('');
    } catch (error: any) {
      console.error('Ошибка сохранения настроек:', error);
      alert(error.message || 'Ошибка сохранения настроек');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveAutoResponse = (command: string) => {
    if (!editingSettings) return;
    
    const newAutoResponses = { ...(editingSettings.autoResponses || {}) };
    delete newAutoResponses[command];
    
    setEditingSettings({
      ...editingSettings,
      autoResponses: Object.keys(newAutoResponses).length > 0 ? newAutoResponses : null
    });
  };

  const handleStartEditTopic = (topic: GroupTopic) => {
    setEditingTopicId(topic.id);
    setEditingTopicName(topic.name || '');
  };

  const handleCancelEditTopic = () => {
    setEditingTopicId(null);
    setEditingTopicName('');
  };

  const handleSaveTopic = async (topicId: string) => {
    if (!selectedGroup) return;
    
    try {
      const updatedTopic = await apiPut(`/telegram-group-settings/${selectedGroup.chatId}/topics/${topicId}`, {
        name: editingTopicName.trim() || null
      });
      
      setTopics(Array.isArray(topics) ? topics.map(t => t.id === topicId ? updatedTopic : t) : [updatedTopic]);
      setEditingTopicId(null);
      setEditingTopicName('');
    } catch (error: any) {
      console.error('Ошибка сохранения топика:', error);
      alert(error.message || 'Ошибка сохранения топика');
    }
  };

  const getGroupSettings = (chatId: string): GroupSettings | null => {
    return groupSettingsMap.get(chatId) || null;
  };

  return (
    <div className="p-4">
      <div className="mb-4">
        <h1 className="text-xl font-bold text-gray-900">Настройка Telegram групп</h1>
        <p className="mt-0.5 text-xs text-gray-500">
          Управление группами и каналами Telegram, из которых бот получает сообщения
        </p>
      </div>

      {/* Информация */}
      <div className="bg-blue-50 rounded-lg border border-blue-200 p-3 mb-4">
        <h3 className="text-xs font-semibold text-blue-900 mb-1.5">ℹ️ Информация</h3>
        <ul className="text-xs text-blue-800 space-y-0.5">
          <li>• По умолчанию бот логирует все сообщения из групп, но не отвечает на них</li>
          <li>• Вы можете настроить логирование и поведение бота для каждой группы отдельно</li>
          <li>• Для работы бота в группе добавьте его в группу и дайте необходимые права</li>
          <li>• Настройки применяются автоматически при получении сообщений из группы</li>
        </ul>
      </div>

      {/* Поиск */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
        <input
          type="text"
          placeholder="Поиск по названию, username или Chat ID..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Список групп */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-base font-semibold text-gray-900">
            Группы и каналы ({filteredGroups.length})
          </h2>
          <button
            onClick={async () => {
              await fetchGroupsAndSettings();
            }}
            className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Обновить
          </button>
        </div>

        {loading ? (
          <div className="text-center py-8 text-gray-500">Загрузка...</div>
        ) : filteredGroups.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {searchQuery ? 'Группы не найдены' : 'Группы не найдены. Сообщения из групп появятся здесь после того, как бот получит сообщения из групп.'}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4">
            {filteredGroups.map((group) => {
              const settings = getGroupSettings(group.chatId);
              return (
                <div
                  key={group.chatId}
                  className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50 flex flex-col"
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${getTypeColor(group.type)} whitespace-nowrap`}>
                          {getTypeLabel(group.type)}
                        </span>
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {group.title || group.username || `Chat ID: ${group.chatId}`}
                        </span>
                        {group.username && (
                          <span className="text-xs text-gray-500 whitespace-nowrap">@{group.username}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                      <button
                        onClick={() => handleOpenSettings(group)}
                        className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 whitespace-nowrap"
                      >
                        Настройки
                      </button>
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 space-y-0.5 flex-1">
                    <div>Chat ID: <span className="font-mono">{group.chatId}</span></div>
                    <div>Сообщений: {group.messageCount}</div>
                    {group.lastMessageDate && (
                      <div>Последнее сообщение: {formatDate(group.lastMessageDate)}</div>
                    )}
                    {settings && (
                      <>
                        <div className="mt-1 pt-1 border-t border-gray-200">
                          <div>Логирование: {settings.loggingEnabled ? '✅' : '❌'}</div>
                          {settings.loggingEnabled && (
                            <>
                              {settings.logOnlyCommands && <div>• Только команды</div>}
                              {settings.logOnlyMentions && <div>• Только упоминания</div>}
                            </>
                          )}
                          <div>Ответы бота: {settings.botResponsesEnabled ? '✅' : '❌'}</div>
                          {settings.botResponsesEnabled && settings.respondOnlyToMentions && (
                            <div>• Только на упоминания</div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Модальное окно настроек группы */}
      {showSettingsModal && editingSettings && selectedGroup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                Настройки группы: {selectedGroup.title || selectedGroup.username || `Chat ID: ${selectedGroup.chatId}`}
              </h2>
            </div>
            
            <div className="p-4 flex-1 overflow-y-auto">
              <div className="space-y-4">
                {/* Настройки логирования */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Настройки логирования</h3>
                  <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={editingSettings.loggingEnabled}
                        onChange={(e) => setEditingSettings({ ...editingSettings, loggingEnabled: e.target.checked })}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">Включить логирование</span>
                    </label>
                    
                    {editingSettings.loggingEnabled && (
                      <>
                        <label className="flex items-center gap-2 ml-6">
                          <input
                            type="checkbox"
                            checked={editingSettings.logOnlyCommands}
                            onChange={(e) => setEditingSettings({ ...editingSettings, logOnlyCommands: e.target.checked, logOnlyMentions: e.target.checked ? false : editingSettings.logOnlyMentions })}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">Логировать только команды</span>
                        </label>
                        
                        <label className="flex items-center gap-2 ml-6">
                          <input
                            type="checkbox"
                            checked={editingSettings.logOnlyMentions}
                            onChange={(e) => setEditingSettings({ ...editingSettings, logOnlyMentions: e.target.checked, logOnlyCommands: e.target.checked ? false : editingSettings.logOnlyCommands })}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">Логировать только упоминания бота</span>
                        </label>
                      </>
                    )}
                  </div>
                </div>

                {/* Настройки поведения бота */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Поведение бота</h3>
                  <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={editingSettings.botResponsesEnabled}
                        onChange={(e) => setEditingSettings({ ...editingSettings, botResponsesEnabled: e.target.checked })}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">Разрешить ответы бота</span>
                    </label>
                    
                    {editingSettings.botResponsesEnabled && (
                      <label className="flex items-center gap-2 ml-6">
                        <input
                          type="checkbox"
                          checked={editingSettings.respondOnlyToMentions}
                          onChange={(e) => setEditingSettings({ ...editingSettings, respondOnlyToMentions: e.target.checked })}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">Отвечать только на упоминания (@botname)</span>
                      </label>
                    )}
                  </div>
                </div>

                {/* Автоответы */}
                {editingSettings.botResponsesEnabled && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Автоответы на команды</h3>
                    <div className="bg-gray-50 rounded-lg p-3 space-y-3">
                      {editingSettings.autoResponses && Object.keys(editingSettings.autoResponses).length > 0 && (
                        <div className="space-y-2">
                          {Object.entries(editingSettings.autoResponses).map(([command, text]) => (
                            <div key={command} className="flex items-start justify-between p-2 bg-white rounded border border-gray-200">
                              <div className="flex-1">
                                <div className="text-xs font-medium text-gray-900">{command}</div>
                                <div className="text-xs text-gray-600 mt-1">{text}</div>
                              </div>
                              <button
                                onClick={() => handleRemoveAutoResponse(command)}
                                className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
                              >
                                Удалить
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      <div className="border-t border-gray-200 pt-3 space-y-2">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Команда (например, /help)</label>
                          <input
                            type="text"
                            value={autoResponseCommand}
                            onChange={(e) => setAutoResponseCommand(e.target.value)}
                            placeholder="/help"
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Текст ответа</label>
                          <textarea
                            value={autoResponseText}
                            onChange={(e) => setAutoResponseText(e.target.value)}
                            placeholder="Текст ответа бота..."
                            rows={3}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <button
                          onClick={() => {
                            if (autoResponseCommand && autoResponseText) {
                              const newAutoResponses = { ...(editingSettings.autoResponses || {}) };
                              newAutoResponses[autoResponseCommand.toLowerCase()] = autoResponseText;
                              setEditingSettings({ ...editingSettings, autoResponses: newAutoResponses });
                              setAutoResponseCommand('');
                              setAutoResponseText('');
                            }
                          }}
                          disabled={!autoResponseCommand || !autoResponseText}
                          className="w-full px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Добавить автоответ
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Топики группы */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Топики группы</h3>
                  <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                    <p className="text-xs text-gray-600 mb-2">
                      Топики автоматически определяются из сообщений. Задайте названия для удобной навигации.
                    </p>
                    {!Array.isArray(topics) || topics.length === 0 ? (
                      <div className="text-xs text-gray-500 text-center py-4">
                        Топики не найдены. Они появятся автоматически, когда в группе появятся сообщения в топиках.
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {topics.map((topic) => (
                          <div key={topic.id} className="flex items-center gap-2 p-2 bg-white rounded border border-gray-200">
                            {editingTopicId === topic.id ? (
                              <>
                                <div className="flex-1">
                                  <input
                                    type="text"
                                    value={editingTopicName}
                                    onChange={(e) => setEditingTopicName(e.target.value)}
                                    placeholder="Название топика"
                                    className="w-full border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        handleSaveTopic(topic.id);
                                      } else if (e.key === 'Escape') {
                                        handleCancelEditTopic();
                                      }
                                    }}
                                    autoFocus
                                  />
                                  <div className="text-xs text-gray-500 mt-1">Thread ID: {topic.threadId}</div>
                                </div>
                                <button
                                  onClick={() => handleSaveTopic(topic.id)}
                                  className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                                >
                                  Сохранить
                                </button>
                                <button
                                  onClick={handleCancelEditTopic}
                                  className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                                >
                                  Отмена
                                </button>
                              </>
                            ) : (
                              <>
                                <div className="flex-1 min-w-0">
                                  <div className="text-xs font-medium text-gray-900 truncate">
                                    {topic.name || `Топик ${topic.threadId}`}
                                  </div>
                                  <div className="text-xs text-gray-500">Thread ID: {topic.threadId}</div>
                                </div>
                                <button
                                  onClick={() => handleStartEditTopic(topic)}
                                  className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 whitespace-nowrap"
                                >
                                  Редактировать
                                </button>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-4 border-t border-gray-200 flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowSettingsModal(false);
                  setSelectedGroup(null);
                  setEditingSettings(null);
                  setAutoResponseCommand('');
                  setAutoResponseText('');
                }}
                className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Отмена
              </button>
              <button
                onClick={handleSaveSettings}
                disabled={saving}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Сохранение...' : 'Сохранить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TelegramGroupSettings;

